"use client";

/**
 * AiAdvisorLauncher — lightweight entry point for SC-NIA.
 *
 * Purpose: keep the full AiAdvisor (which pulls in @ai-sdk/react + ai +
 * framer-motion for a ~700-line chat panel) OUT of the initial JS bundle.
 * The launcher renders a single fixed button; only when the user clicks it
 * do we dynamically load the heavy AiAdvisor chat panel.
 *
 * Savings on initial load (homepage + every other public page):
 *   - @ai-sdk/react + ai ... ~200 KB gzipped deferred
 *   - full AiAdvisor tree  ... ~40 KB deferred
 *   - extra framer-motion useChat hooks ... ~30 KB deferred
 */

import "@/lib/zod-jitless";
import { useState, type ReactElement } from "react";
import dynamic from "next/dynamic";
import { Sparkles, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useTranslation } from "@/lib/i18n";

const AiAdvisor = dynamic(
  () => import("./AiAdvisor").then((m) => ({ default: m.AiAdvisor })),
  { ssr: false }
);

export function AiAdvisorLauncher(): ReactElement {
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation();

  if (mounted) {
    // Full SC-NIA is now in charge of its own launcher + panel rendering.
    return <AiAdvisor />;
  }

  return (
    <div className="fab-lift fab-chat-idle fixed bottom-4 right-4 z-50 sm:bottom-6 sm:right-6">
      <button
        type="button"
        onClick={() => {
          trackEvent("ai_advisor_open");
          setMounted(true);
        }}
        className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-150 hover:scale-105 active:scale-95"
        style={{ background: "linear-gradient(135deg, #00d4aa, #0066ff)" }}
        aria-label={t("scnia_open_aria")}
      >
        <span
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: "rgba(0,212,170,0.3)", animationDuration: "2s" }}
          aria-hidden
        />
        <Sparkles className="w-5 h-5 text-text-primary relative" aria-hidden />
        <X className="w-5 h-5 text-text-primary absolute opacity-0" aria-hidden />
      </button>
    </div>
  );
}
